/**
 * Module i18n — point d'entrée du système multi-langues.
 *
 * Conventions :
 *   - Locale par défaut : fr-CH (Suisse romande, cible commerciale initiale)
 *   - Fallback chain : fr-CH → fr → en
 *   - 7 namespaces par locale : common, auth, home, errors, roles, actions, time
 *   - fr-CH est chargé eagerly (toujours présent comme fallback)
 *   - Autres locales chargées lazily à la demande
 */

import commonFR_CH from './fr-CH/common.json';
import authFR_CH from './fr-CH/auth.json';
import homeFR_CH from './fr-CH/home.json';
import errorsFR_CH from './fr-CH/errors.json';
import rolesFR_CH from './fr-CH/roles.json';
import actionsFR_CH from './fr-CH/actions.json';
import timeFR_CH from './fr-CH/time.json';
import chantiersFR_CH from './fr-CH/chantiers.json';
import pointagesFR_CH from './fr-CH/pointages.json';
import journalFR_CH from './fr-CH/journal.json';
import fichesHeuresFR_CH from './fr-CH/fiches_heures.json';
import mesPointagesFR_CH from './fr-CH/mes_pointages.json';
import livraisonsFR_CH from './fr-CH/livraisons.json';
import demandesFR_CH from './fr-CH/demandes.json';
import tempsFR_CH from './fr-CH/temps.json';
import vehiculesFR_CH from './fr-CH/vehicules.json';
import paieSettingsFR_CH from './fr-CH/paie_settings.json';
import gestionFR_CH from './fr-CH/gestion.json';
import notificationsFR_CH from './fr-CH/notifications.json';
import demoFR_CH from './fr-CH/demo.json';

/** Schéma de tous les messages (typage strict des clés) */
export type MessageSchema = {
  common: typeof commonFR_CH;
  auth: typeof authFR_CH;
  home: typeof homeFR_CH;
  errors: typeof errorsFR_CH;
  roles: typeof rolesFR_CH;
  actions: typeof actionsFR_CH;
  time: typeof timeFR_CH;
  chantiers: typeof chantiersFR_CH;
  pointages: typeof pointagesFR_CH;
  journal: typeof journalFR_CH;
  fiches_heures: typeof fichesHeuresFR_CH;
  mes_pointages: typeof mesPointagesFR_CH;
  livraisons: typeof livraisonsFR_CH;
  demandes: typeof demandesFR_CH;
  temps: typeof tempsFR_CH;
  vehicules: typeof vehiculesFR_CH;
  paie_settings: typeof paieSettingsFR_CH;
  gestion: typeof gestionFR_CH;
  notifications: typeof notificationsFR_CH;
  demo: typeof demoFR_CH;
};

/** Locales supportées (codes BCP 47) */
export const SUPPORTED_LOCALES = [
  'fr-CH',
  'fr',
  'de-CH',
  'de',
  'it-CH',
  'it',
  'en',
  'nl',
  'es',
  'pt',
  'sq-XK',
] as const;

export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

/** Locale par défaut (cible commerciale Stafflow) */
export const DEFAULT_LOCALE: SupportedLocale = 'fr-CH';

/** Chaîne de fallback : si une clé manque, on remonte cette chaîne */
export const FALLBACK_LOCALE: SupportedLocale[] = ['fr-CH', 'fr', 'en'];

/** Metadata utilisateur (label affichable, drapeau, etc.) */
export interface LocaleMetadata {
  code: SupportedLocale;
  label: string;
  flag: string;
  rtl?: boolean;
}

export const LOCALE_METADATA: LocaleMetadata[] = [
  { code: 'fr-CH', label: 'Français (Suisse)', flag: '🇨🇭' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'de-CH', label: 'Deutsch (Schweiz)', flag: '🇨🇭' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'it-CH', label: 'Italiano (Svizzera)', flag: '🇨🇭' },
  { code: 'it', label: 'Italiano', flag: '🇮🇹' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'nl', label: 'Nederlands', flag: '🇳🇱' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'pt', label: 'Português', flag: '🇵🇹' },
  { code: 'sq-XK', label: 'Shqip (Kosovë)', flag: '🇽🇰' },
];

/** Messages fr-CH chargés au démarrage (locale par défaut + fallback) */
export const messagesFR_CH: MessageSchema = {
  common: commonFR_CH,
  auth: authFR_CH,
  home: homeFR_CH,
  errors: errorsFR_CH,
  roles: rolesFR_CH,
  actions: actionsFR_CH,
  time: timeFR_CH,
  chantiers: chantiersFR_CH,
  pointages: pointagesFR_CH,
  journal: journalFR_CH,
  fiches_heures: fichesHeuresFR_CH,
  mes_pointages: mesPointagesFR_CH,
  livraisons: livraisonsFR_CH,
  demandes: demandesFR_CH,
  temps: tempsFR_CH,
  vehicules: vehiculesFR_CH,
  paie_settings: paieSettingsFR_CH,
  gestion: gestionFR_CH,
  notifications: notificationsFR_CH,
  demo: demoFR_CH,
};

/**
 * Charge dynamiquement les messages d'une locale.
 * Les locales non-fr-CH sont chargées lazily.
 */
export async function loadLocaleMessages(locale: SupportedLocale): Promise<MessageSchema> {
  if (locale === 'fr-CH') return messagesFR_CH;

  // import dynamique des 20 namespaces de la locale demandée
  const [common, auth, home, errors, roles, actions, time, chantiers, pointages, journal, fiches_heures, mes_pointages, livraisons, demandes, temps, vehicules, paie_settings, gestion, notifications, demo] = await Promise.all([
    import(`./${locale}/common.json`).then((m) => m.default),
    import(`./${locale}/auth.json`).then((m) => m.default),
    import(`./${locale}/home.json`).then((m) => m.default),
    import(`./${locale}/errors.json`).then((m) => m.default),
    import(`./${locale}/roles.json`).then((m) => m.default),
    import(`./${locale}/actions.json`).then((m) => m.default),
    import(`./${locale}/time.json`).then((m) => m.default),
    import(`./${locale}/chantiers.json`).then((m) => m.default),
    import(`./${locale}/pointages.json`).then((m) => m.default),
    import(`./${locale}/journal.json`).then((m) => m.default),
    import(`./${locale}/fiches_heures.json`).then((m) => m.default),
    import(`./${locale}/mes_pointages.json`).then((m) => m.default),
    import(`./${locale}/livraisons.json`).then((m) => m.default),
    import(`./${locale}/demandes.json`).then((m) => m.default),
    import(`./${locale}/temps.json`).then((m) => m.default),
    import(`./${locale}/vehicules.json`).then((m) => m.default),
    import(`./${locale}/paie_settings.json`).then((m) => m.default),
    import(`./${locale}/gestion.json`).then((m) => m.default),
    import(`./${locale}/notifications.json`).then((m) => m.default),
    import(`./${locale}/demo.json`).then((m) => m.default),
  ]);

  return { common, auth, home, errors, roles, actions, time, chantiers, pointages, journal, fiches_heures, mes_pointages, livraisons, demandes, temps, vehicules, paie_settings, gestion, notifications, demo };
}

/** Détecte la locale initiale : localStorage > navigateur > défaut */
export function detectInitialLocale(): SupportedLocale {
  if (typeof window === 'undefined') return DEFAULT_LOCALE;

  const stored = localStorage.getItem('user_locale');
  if (stored && (SUPPORTED_LOCALES as readonly string[]).includes(stored)) {
    return stored as SupportedLocale;
  }

  // Match navigateur : priorité aux variantes régionales (fr-CH avant fr)
  const navLocales = navigator.languages || [navigator.language];
  for (const navLoc of navLocales) {
    // Match exact d'abord
    if ((SUPPORTED_LOCALES as readonly string[]).includes(navLoc)) {
      return navLoc as SupportedLocale;
    }
    // Match par préfixe (ex. 'fr-FR' -> 'fr')
    const prefix = navLoc.split('-')[0];
    if ((SUPPORTED_LOCALES as readonly string[]).includes(prefix)) {
      return prefix as SupportedLocale;
    }
  }

  return DEFAULT_LOCALE;
}
